import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, Dispatch, SetStateAction } from 'react';
import useAuth from '../../../shared/src/hooks/useAuth';
import { obtenerParametros } from '../services/obtenerParametros.service';

// Define the shape of your single state object
export type CierreFinancieroState = {
    [x: string]: ReactNode;
    totalIngresosAdicionales: number;
    salarioMensual: number;
    valorVivienda: number;
    tiempoSeleccionado: number | null;
    municipioVive: string;
    municipioCompra: string;
    deudas: number;
    pagoMensual: number;
    cesantias: number;
    ahorros: number;
    primas: string;
    cantidadPrimas: number;
    subsidioEmpresa: number;
    ahorroMensual: number;
    cesantiasFuturas: string;
    ingresosAdicionales: string;
    tasaEfectivaAnual: number;
    plazoCredito: number;
    porcentajeEndeudamiento: number;
    subsidioDepartamento: number;
    totalCreditoHipotecario: number;
    // Valores calculados para proyección y resultados
    totalAhorrosValue: number;
    sumaSubsidiosValue: number;
    valorTotalCreditoValue: number;
    subsidioCajaValue: number;
    subsidioMunicipioValue: number;
    valorSalarioMinimo: number;
    topeSubsidio: number;
};

// Utilidad para evitar duplicación de estado inicial
const getDefaultCierreFinanciero = (overrides: Partial<CierreFinancieroState> = {}): CierreFinancieroState => ({
    totalIngresosAdicionales: 0,
    salarioMensual: 0,
    valorVivienda: 0,
    tiempoSeleccionado: null,
    municipioVive: '',
    municipioCompra: '',
    deudas: 0,
    pagoMensual: 0,
    cesantias: 0,
    ahorros: 0,
    primas: 'no',
    cantidadPrimas: 0,
    subsidioEmpresa: 0,
    ahorroMensual: 0,
    cesantiasFuturas: 'no',
    ingresosAdicionales: '',
    tasaEfectivaAnual: 0,
    plazoCredito: 0,
    porcentajeEndeudamiento: 0,
    subsidioDepartamento: 0,
    totalCreditoHipotecario: 0,
    totalAhorrosValue: 0,
    sumaSubsidiosValue: 0,
    valorTotalCreditoValue: 0,
    subsidioCajaValue: 0,
    subsidioMunicipioValue: 0,
    valorSalarioMinimo: 0,
    topeSubsidio: 0,
    ...overrides
});

export type CierreFinancieroContextType = {
    cierreFinanciero: CierreFinancieroState | null;
    setCierreFinanciero: Dispatch<SetStateAction<CierreFinancieroState | null>>;
    error: string | null;
};

export type CierreFinancieroProviderProps = {
    children?: ReactNode;
};



const STORAGE_KEY = 'cierreFinanciero';

export const CierreFinancieroContext = createContext<CierreFinancieroContextType | undefined>(undefined);

export const CierreFinancieroProvider = ({ children }: CierreFinancieroProviderProps) => {
    const [error, setError] = useState<string | null>(null);
    // Estado inicial: null, se poblará desde el servicio
    const [cierreFinanciero, setCierreFinanciero] = useState<CierreFinancieroState | null>(null);

    const { auth, isAuthenticated } = useAuth();
    useEffect(() => {
        if (!auth || !auth.status || !isAuthenticated) {
            return;
        }
        if (cierreFinanciero !== null) {
            return;
        }
        const fetchParametros = async () => {
            try {
                const parametros = await obtenerParametros();
                if (!parametros || typeof parametros !== 'object') {
                    setError('Respuesta de parámetros inválida');
                    setCierreFinanciero(getDefaultCierreFinanciero());
                    return;
                }
                if (parametros.status === 200 && Array.isArray(parametros.data)) {
                    const paramObj = parametros.data.reduce((acc: any, item: any) => {
                        if (!item || typeof item.name !== 'string') return acc;
                        acc[item.name] = isNaN(Number(item.value)) ? item.value : Number(item.value);
                        return acc;
                    }, {});
                    setCierreFinanciero(getDefaultCierreFinanciero({
                        tasaEfectivaAnual: typeof paramObj.tasaEfectivaAnual === 'number' ? paramObj.tasaEfectivaAnual : 0,
                        plazoCredito: typeof paramObj.plazoCredito === 'number' ? paramObj.plazoCredito : 0,
                        porcentajeEndeudamiento: typeof paramObj.porcentajeEndeudamiento === 'number' ? paramObj.porcentajeEndeudamiento : 0,
                        subsidioDepartamento: typeof paramObj.subsidioDepartamento === 'number' ? paramObj.subsidioDepartamento : 0,
                        subsidioCajaValue: typeof paramObj.subsidioCajaValue === 'number' ? paramObj.subsidioCajaValue : 0,
                        subsidioMunicipioValue: typeof paramObj.subsidioMunicipioValue === 'number' ? paramObj.subsidioMunicipioValue : 0,
                        valorSalarioMinimo: typeof paramObj.valorSalarioMinimo === 'number' ? paramObj.valorSalarioMinimo : 0,
                        topeSubsidio: typeof paramObj.topeSubsidio === 'number' ? paramObj.topeSubsidio : 0,
                    }));
                } else {
                    setError('Respuesta inesperada de parámetros');
                    setCierreFinanciero(getDefaultCierreFinanciero());
                }
            } catch (error) {
                setError('Error al obtener parámetros');
                setCierreFinanciero(getDefaultCierreFinanciero());
            }
        };
        fetchParametros();
    }, [auth, cierreFinanciero]);

    // Sincroniza los valores calculados automáticamente en el contexto
    useEffect(() => {
        if (!cierreFinanciero) return;
        // Importa aquí para evitar problemas de dependencias circulares
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const { totalAhorros, subsidioCaja, subsidioMunicipio, subsidioDepartamento, sumaSubsidios, valorTotalCredito } = require('../utils/calculosFinancieros');
        // Calcula subsidios usando el objeto completo
        const subsidioCajaValue = subsidioCaja(cierreFinanciero);
        const subsidioMunicipioValue = subsidioMunicipio(cierreFinanciero);
        const subsidioDepartamentoValue = subsidioDepartamento(cierreFinanciero);
        const sumaSubsidiosValue = sumaSubsidios({
            ...cierreFinanciero,
            subsidioCajaValue,
            subsidioMunicipioValue,
            subsidioDepartamento: subsidioDepartamentoValue,
        });
        const valorTotalCreditoValue = valorTotalCredito(cierreFinanciero);
        const totalAhorrosValue = totalAhorros(cierreFinanciero);
        setCierreFinanciero((prev) => prev ? ({
            ...prev,
            totalAhorrosValue,
            sumaSubsidiosValue,
            valorTotalCreditoValue,
            subsidioCajaValue,
            subsidioMunicipioValue,
            subsidioDepartamento: subsidioDepartamentoValue,
        }) : prev);
        // Solo cuando cambian los datos base relevantes
    }, [
        cierreFinanciero?.salarioMensual,
        cierreFinanciero?.municipioCompra,
        cierreFinanciero?.valorVivienda,
        cierreFinanciero?.tiempoSeleccionado,
        cierreFinanciero?.ahorros,
        cierreFinanciero?.ahorroMensual,
        cierreFinanciero?.cantidadPrimas,
        cierreFinanciero?.cesantias,
        cierreFinanciero?.cesantiasFuturas,
        cierreFinanciero?.deudas,
        cierreFinanciero?.pagoMensual,
        cierreFinanciero?.subsidioEmpresa,
        cierreFinanciero?.subsidioDepartamento,
        cierreFinanciero?.totalIngresosAdicionales,
        cierreFinanciero?.ingresosAdicionales,
        cierreFinanciero?.primas,
        cierreFinanciero?.plazoCredito,
        cierreFinanciero?.tasaEfectivaAnual,
        cierreFinanciero?.porcentajeEndeudamiento,
    ]);

    // Persist to sessionStorage on every change
    // Debounce para evitar escrituras excesivas en sessionStorage
    const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        if (!cierreFinanciero) return;
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cierreFinanciero));
        }, 300);
        // Limpieza al desmontar
        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, [cierreFinanciero]);

    return (
        <CierreFinancieroContext.Provider value={{ cierreFinanciero, setCierreFinanciero, error }}>
            {error && <div style={{color:'red'}}>{error}</div>}
            {children}
        </CierreFinancieroContext.Provider>
    );
};

export const useCierreFinanciero = () => {
    const context = useContext(CierreFinancieroContext);
    if (!context) {
        throw new Error('useCierreFinanciero must be used within a CierreFinancieroProvider');
    }
    return context;
};

// getInitialState ahora es asíncrono y toma valores de fetchParametros si no hay sessionStorage
export const getInitialState = async (): Promise<CierreFinancieroState> => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
        try {
            return JSON.parse(stored);
        } catch {
            // fallback to defaults if corrupted
        }
    }
    try {
        const parametros = await obtenerParametros();
        if (parametros.status === 200 && Array.isArray(parametros.data)) {
            const paramObj = parametros.data.reduce((acc: any, item: any) => {
                if (!item || typeof item.name !== 'string') return acc;
                acc[item.name] = isNaN(Number(item.value)) ? item.value : Number(item.value);
                return acc;
            }, {});
            return getDefaultCierreFinanciero({
                tasaEfectivaAnual: typeof paramObj.tasaEfectivaAnual === 'number' ? paramObj.tasaEfectivaAnual : 10.6,
                plazoCredito: typeof paramObj.plazoCredito === 'number' ? paramObj.plazoCredito : 20,
                porcentajeEndeudamiento: typeof paramObj.porcentajeEndeudamiento === 'number' ? paramObj.porcentajeEndeudamiento : 0.4
            });       
        }
    } catch {
        // fallback to defaults if error
    }
    return getDefaultCierreFinanciero({
        tasaEfectivaAnual: 10.6,
        plazoCredito: 20,
        porcentajeEndeudamiento: 0.4
    });
};
